import React, { useEffect, useState } from 'react';
import API from '../services/apiBase';
import {
  bootstrapAuth,
  dropEntraSession,
  entraAccount,
  getAccessToken,
  isEntraEnabled,
  isLocalLoginEnabled,
  signInWithMicrosoft,
  signOut,
  storeLocalSession,
  storeUserProfile,
} from '../services/authSession';

const parseError = async (response, fallback) => {
  const raw = await response.text();
  if (!raw) return fallback;
  try {
    const data = JSON.parse(raw);
    return data.detail || data.error || fallback;
  } catch (_) {
    return raw;
  }
};

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [entraEnabled, setEntraEnabled] = useState(false);
  const [localEnabled, setLocalEnabled] = useState(true);
  const [completingMicrosoft, setCompletingMicrosoft] = useState(false);

  // After a Microsoft redirect lands back here with an account, exchange the
  // access token for the IncurredIQ profile and enter the app.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await bootstrapAuth();
        if (cancelled) return;
        const entra = isEntraEnabled();
        setEntraEnabled(entra);
        setLocalEnabled(isLocalLoginEnabled());
        if (entra && entraAccount()) {
          setCompletingMicrosoft(true);
          const token = await getAccessToken();
          if (!token) {
            setCompletingMicrosoft(false);
            return;
          }
          const response = await fetch(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!response.ok) {
            const message = await parseError(response, 'Sign-in was not accepted');
            throw new Error(message);
          }
          const user = await response.json();
          storeUserProfile(user);
          if (user?.client_id && !localStorage.getItem('active_client_id')) {
            localStorage.setItem('active_client_id', user.client_id);
          }
          window.location.href = '/';
          return;
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Sign-in failed');
          setCompletingMicrosoft(false);
          // The identity was rejected (typically: not invited). Forget it on
          // this tab so the page does not keep retrying it, and so the
          // password form below works straight away.
          await dropEntraSession();
        }
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleMicrosoft = async () => {
    setError('');
    setLoading(true);
    try {
      await signInWithMicrosoft();
    } catch (err) {
      setError(err.message || 'Sign-in failed');
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        throw new Error(await parseError(response, 'Login failed'));
      }

      const data = await response.json();
      storeLocalSession(data.access_token, data.user);
      // Only set active_client_id if none is saved — preserve previous client selection
      if (data?.user?.client_id && !localStorage.getItem('active_client_id')) {
        localStorage.setItem('active_client_id', data.user.client_id);
      }

      window.location.href = '/';
    } catch (err) {
      const isNetworkError =
        err?.message === 'Failed to fetch' ||
        (typeof err?.message === 'string' && err.message.includes('NetworkError'));
      setError(
        isNetworkError
          ? 'Unable to reach backend API. Make sure FastAPI is running on http://127.0.0.1:8001.'
          : err.message
      );
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to IncurredIQ
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Use the account your administrator set up for you.
          </p>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-800">{error}</div>
            {entraEnabled && entraAccount() && (
              <button
                type="button"
                onClick={() => signOut()}
                className="mt-3 text-sm text-red-700 underline"
              >
                Sign out and try another account
              </button>
            )}
          </div>
        )}

        {!ready && (
          <div className="text-center text-sm text-gray-500">Checking sign-in options…</div>
        )}

        {ready && completingMicrosoft && !error && (
          <div className="text-center text-sm text-gray-600">Completing sign-in…</div>
        )}

        {ready && !completingMicrosoft && entraEnabled && (
          <div>
            <button
              type="button"
              onClick={handleMicrosoft}
              disabled={loading}
              className="group relative w-full flex justify-center items-center gap-2 py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-900 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
                <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
              </svg>
              {loading ? 'Redirecting…' : 'Sign in with your work email'}
            </button>
            <p className="mt-2 text-center text-xs text-gray-500">
              Works with Microsoft 365, Google Workspace, or an emailed sign-in code. No IncurredIQ password needed.
              First time here? On the next page choose <em>No account? Create one</em> and use the email you were invited with.
            </p>
            {localEnabled && (
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-xs text-gray-500">
                  <span className="bg-gray-50 px-2">or use an IncurredIQ password</span>
                </div>
              </div>
            )}
          </div>
        )}

        {ready && !completingMicrosoft && localEnabled && (
          <form className="space-y-6" onSubmit={handleLogin}>
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="email" className="sr-only">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className={`${inputClass} rounded-t-md`}
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="password" className="sr-only">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className={`${inputClass} rounded-b-md`}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
            </div>
          </form>
        )}

        {ready && !localEnabled && !entraEnabled && (
          <div className="text-center text-sm text-red-700">
            No sign-in method is enabled on this deployment. Contact your administrator.
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
